import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";

import { useCountdown, useIdentityClient, useToken, useAuthentication } from "../../hooks";
import { type PasswordResetVerifyData, tokenResponseSchema } from "../../schemas";

import { PasswordResetVerifyTemplate } from "./password-reset-verify.template";

const TIMER_INTERVAL = 59;

export const PasswordResetVerify = () => {
	const intl = useIntl();
	const navigate = useNavigate();

	const { client } = useIdentityClient();
	const { setToken } = useToken();
	const { logout } = useAuthentication();

	const [searchParams] = useSearchParams();
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const username = searchParams.get("username") || "";
	const countdown = useCountdown(TIMER_INTERVAL);

	const handleSubmit = async (data: PasswordResetVerifyData) => {
		try {
			const response = await client.validatePasswordResetOtp(data);

			if (response.success === false) {
				setErrorMessage(
					intl.formatMessage({
						id: "password-reset-verify.error",
						defaultMessage: "The code is incorrect. Please try again.",
					}),
				);
				return;
			}

			const tokenResponse = tokenResponseSchema.parse(response.data);

			setToken(tokenResponse.access_token);
			navigate(`/password-reset-complete`);
		} catch (error) {
			setErrorMessage(
				intl.formatMessage({
					id: "password-reset-verify.error",
					defaultMessage: "The code is incorrect. Please try again.",
				}),
			);
			console.error("Error validating password reset OTP", error);
			return;
		}
	};

	const handleResendCode = () => {
		countdown.reset();
		client.initiatePasswordReset({ email: username });
	};

	const handleBackToLogin = () => {
		logout();
		navigate("/login");
	};

	return (
		<PasswordResetVerifyTemplate
			timeLeft={countdown.timeLeft}
			onSubmit={handleSubmit}
			onResendCode={handleResendCode}
			onBackToLogin={handleBackToLogin}
			username={username}
			errorMessage={errorMessage}
		/>
	);
};
